'use client';

import React from 'react';

export interface OTPInputProps {
  length?: number;
  value?: string;
  /** `error` shows brick ring (pair with shake at app layer). */
  state?: 'default' | 'error' | 'success';
  activeIndex?: number;
  /** Pass to make the cells the real, focusable/typable input — a single
   * invisible native `<input>` overlays the whole cell row, so tapping
   * anywhere focuses it and the cells simply mirror its value. Omit for the
   * old presentational-only mode (caller wires its own input elsewhere). */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  'aria-label'?: string;
  'data-testid'?: string;
  style?: React.CSSProperties;
}

/**
 * 6-cell OTP input. With `onChange`, the cells ARE the real input (one
 * invisible native `<input>` overlaid on top) — no separate text field
 * needed. Without it, purely presentational (caller wires input elsewhere).
 */
export function OTPInput({
  length = 6,
  value = '',
  state = 'default',
  activeIndex = value.length,
  onChange,
  'aria-label': ariaLabel,
  'data-testid': testId,
  style = {},
}: OTPInputProps) {
  const chars = value.split('');
  const borderFor = (i: number) => {
    if (state === 'error') return 'var(--error)';
    if (state === 'success') return 'var(--success)';
    if (i === activeIndex) return 'var(--brand)';
    return 'var(--border-strong)';
  };

  return (
    <div
      dir="ltr"
      style={{
        position: 'relative',
        display: 'flex',
        gap: 'var(--sp-3)',
        justifyContent: 'center',
        ...style,
      }}
    >
      {Array.from({ length }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 48,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-title1)',
            fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-primary)',
            background: 'var(--bg-surface)',
            border: `1.5px solid ${borderFor(i)}`,
            borderRadius: 'var(--r-md)',
            boxShadow:
              i === activeIndex && state === 'default' ? '0 0 0 3px var(--brand-wash)' : 'none',
            transition: 'border-color var(--dur-micro), box-shadow var(--dur-micro)',
          }}
        >
          {chars[i] || ''}
        </div>
      ))}
      {onChange && (
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={length}
          value={value}
          onChange={onChange}
          aria-label={ariaLabel}
          data-testid={testId}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            border: 'none',
            outline: 'none',
            fontSize: 16,
            cursor: 'text',
          }}
        />
      )}
    </div>
  );
}
